import {
  deleteCalendarItemMutation,
  getCalendarItem,
  listCalendarItems,
} from '@akin-home/calendar/combined';
import { addDays, addWeeks, startOfDay } from 'date-fns';
import { QueryClient } from 'react-query';
import { apiQueryKeys } from '../../../calendar/combined/getCalendarItemAPIFunctions';
import { CalendarItemType } from '../../../models';
import { createOutputTextItem } from '../../ChatItem/functions';
import { AddChatItemFunction } from '../../types';
import { DeleteCalendarItemCommand } from '../types';

/** Handles an add_calendar_item command */
const deleteCalendarItemCommand = async (
  /** The command data */
  command: DeleteCalendarItemCommand,

  habitatID: string,
  /** Query client for invalidating the cache upon item create */
  queryClient: QueryClient | undefined,

  addChatItem: AddChatItemFunction
) => {
  const result = DeleteCalendarItemCommand.safeParse(command);

  if (!result.success) {
    console.log('Command Error: ', result);
    return;
  }

  const { data } = result.data;

  const { id, title, not_found_message, start_date } = data;

  const type = data.type ? (data.type as CalendarItemType) : undefined;

  let refresh_type: CalendarItemType | undefined = type
    ? (type as CalendarItemType)
    : undefined;

  if (id) {
    const item = await getCalendarItem({ id, type });

    if (item) {
      await deleteCalendarItemMutation(id, item.type);

      refresh_type = item.type;
    }
  } else {
    /** Parse start date if set */
    const start = start_date ? new Date(start_date) : undefined;

    /** If a date has been specified, filter events for that day. Otherwise filter for the next 7 days */
    const filter = start
      ? {
          from: startOfDay(start).toISOString(),
          to: startOfDay(addDays(start, 1)).toISOString(),
          title,
        }
      : {
          from: startOfDay(new Date()).toISOString(),
          to: startOfDay(addWeeks(new Date(), 1)).toISOString(),
          title,
        };

    /** Search for the best matching item */
    const items = await listCalendarItems({
      types: type ? [type] : undefined,
      query: {
        habitatID,
        filter,
      },
    });

    const first_item = items?.[0];
    if (!first_item) {
      not_found_message && addChatItem(createOutputTextItem(not_found_message));
      return;
    }

    /** Delete the item */
    await deleteCalendarItemMutation(first_item.id, first_item.type);
    refresh_type = first_item.type;
  }

  refresh_type && queryClient?.invalidateQueries(apiQueryKeys[refresh_type]);
};

export default deleteCalendarItemCommand;
